/**
 * Действия режима «Мои ошибки».
 *
 * Каждая стадия цикла — отдельный прогон набора. Микросессия остаётся в
 * состоянии экрана, поэтому её нельзя потерять при перерисовке.
 */

import { useCallback } from 'react';
import type { TypingSession } from '@type2x/typing-engine';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import { advanceStage, finishMistakes, mistakesScreen } from '../state/mistakes.js';
import type { SessionPayload } from '@type2x/feature-sync';
import type { Workspace } from '@type2x/feature-personal-workspace';

/** Действия режима, которые приложение передаёт экранам. */
export interface MistakesActions {
  readonly onStartMistakes: () => void;
  readonly onMistakeStageDone: (session: TypingSession) => void;
}

/** Что нужно режиму от приложения. */
export interface MistakesDeps {
  readonly workspace: Workspace | null;
  readonly commit: (next: Workspace) => Promise<void>;
  readonly screen: AppScreen;
  readonly setScreen: (screen: AppScreen) => void;
  readonly submit: (payload: SessionPayload) => Promise<unknown>;
}

export function useMistakes(runtime: Runtime, deps: MistakesDeps): MistakesActions {
  const { workspace, commit, screen, setScreen, submit } = deps;

  const onStartMistakes = useCallback(() => {
    if (workspace === null) return;

    // Слабых мест может не остаться: тогда режиму нечего показывать и
    // пользователь остаётся там, где был.
    const next = mistakesScreen(runtime, workspace);
    if (next !== null) setScreen(next);
  }, [runtime, workspace, setScreen]);

  const onMistakeStageDone = useCallback(
    (run: TypingSession) => {
      if (workspace === null || screen.name !== 'my-mistakes') return;

      const session = advanceStage(screen.session, run);
      if (!session.finished) {
        setScreen({ ...screen, session });
        return;
      }

      const transition = finishMistakes(runtime, workspace, session, screen.sessionId);
      void commit(transition.workspace);
      setScreen(transition.screen);

      // Отправка идёт после показа результата: сеть не задерживает экран.
      if (transition.sessionPayload !== undefined) {
        void submit(transition.sessionPayload);
      }
    },
    [runtime, workspace, screen, commit, setScreen, submit],
  );

  return { onStartMistakes, onMistakeStageDone };
}
